import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatFacades from '../../internal';

@Injectable()
export class ImageEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static addImage(store: Store,
                  page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                  layer: FlatModels.LayerEntityModels.Layer) {
    // Create New Image
    const newImage: FlatModels.ImageEntityModels.Image = {
      id: uuid(),
      sequenceNumber: layer.images.length + 1,
      default: false,
      annotations: [],
      background: {
        color: null,
        blendMode: FlatModels.ImageEntityModels.BlendMode.NONE,
      },
      pois: [],
      layer: layer.id,
    };
    
    // Update Layer
    if (!layer.images) {
      layer.images = [newImage.id];
    } else {
      layer.images.push(newImage.id);
    }
    
    // Get Data
    const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    const typedMedias = store.selectSnapshot(FlatStates.MediaEntityState.typedEntities$);
    
    // Check for Default Image
    let firstImage: FlatModels.ImageEntityModels.Image | null = null;
    let alreadySelected = false;
    for (const layer of page.layers) {
      const layerEntity = structuredClone(typedLayers[layer]);
      if (layerEntity) {
        for (const image of (layerEntity.images || [])) {
          const imageEntity = structuredClone(typedImages[image]);
          if (!firstImage) {
            firstImage = imageEntity;
          }
          if (imageEntity && imageEntity.default) {
            alreadySelected = true;
            break;
          }
        }
      }
      if (alreadySelected) {
        break;
      }
    }
    
    // Update Page Media Type
    const updatePage = structuredClone(page);
    let mediaTypeChanged = false;
    if (firstImage && firstImage.media) {
      // Get Media
      const media = typedMedias[firstImage.media];
      
      if (updatePage.mediaType !== media.type) {
        mediaTypeChanged = true;
        
        // Update Page
        updatePage.mediaType = media.type;
      }
    }
    
    // Add Image And Update Layer
    return store.dispatch(new FlatActions.ImageEntityActions.AddEntity(newImage)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(layer))
      ),
      mergeMap((data) => {
        if (!alreadySelected && firstImage) {
          firstImage.default = true;
          return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(firstImage))
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (mediaTypeChanged) {
          return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(updatePage));
        }
        return of(data);
      }),
      mergeMap((data) => {
        store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded(layer.slide));
        store.dispatch(new FlatActions.SlideAuthorActions.AddTypedLayerExpanded(layer.id));
        store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layer.position));
        store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(layer.images.length - 1));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image))
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newImage.id));
        return of(data);
      }),
    );
  }
  
  protected static copyImage(store: Store,
                             mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                             id: string,
                             page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                             layer: FlatModels.LayerEntityModels.Layer,
                             withPois: boolean,
                             withAnnotations: boolean,
                             clusterParentUpdate = false) {
    // Check If Image Mapping Exits
    if (mappings.image[id]) {
      return of(undefined);
    }
    
    // Get Data
    const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    
    // Get Image
    const image = structuredClone(typedImages[id]);
    
    // Update Image Mapping
    mappings.image[id] = uuid();
    
    // Update Image
    image.id = mappings.image[id];
    image.layer = layer.id;
    image.sequenceNumber = layer.images.length + 1;
    image.default = false;
    
    // Update Layer
    layer.images.push(image.id);
    
    // Annotation Fix
    const annotations = structuredClone(image.annotations);
    image.annotations = [];
    
    // Poi Fix
    const pois = structuredClone(image.pois);
    image.pois = [];
    
    // Check for Default Image
    let firstImage: FlatModels.ImageEntityModels.Image | null = null;
    let alreadySelected = false;
    for (const layer of page.layers) {
      const layerEntity = structuredClone(typedLayers[layer]);
      if (layerEntity) {
        for (const image of layerEntity.images) {
          const imageEntity = structuredClone(typedImages[image]);
          if (!firstImage) {
            firstImage = imageEntity;
          }
          if (imageEntity && imageEntity.default) {
            alreadySelected = true;
            break;
          }
        }
      }
      if (alreadySelected) {
        break;
      }
    }
    
    // Copy Image
    return of(image.id).pipe(
      mergeMap((data) => {
        if (withAnnotations) {
          return FlatFacades.AnnotationEntityFacade.copyAnnotations(store, mappings, annotations, page, image, undefined, true)
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (!withPois) {
          return of(data);
        }
        return FlatFacades.PoiEntityFacade.copyPois(store, mappings, pois, page, [image], withAnnotations, true);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.ImageEntityActions.AddEntity(image))
      ),
      mergeMap((data) => {
        if (!alreadySelected && firstImage) {
          firstImage.default = true;
          return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(firstImage))
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(layer));
      }),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
      mergeMap(() => of(image.id))
    );
  }
  
  static copyImages(store: Store,
                    mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                    ids: string[],
                    page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                    layer: FlatModels.LayerEntityModels.Layer,
                    withPois: boolean,
                    withAnnotations: boolean,
                    clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Copy Images
    return forkJoin(ids.map(id => ImageEntityFacade.copyImage(store, mappings, id, page, layer, withPois, withAnnotations, true))).pipe(
      mergeMap((newIds) => {
        const selectIds = newIds.reduce((arr, id) => {
          if (id !== undefined) {
            arr.push(id);
          }
          return arr;
        }, [] as string[]);
        return of(selectIds);
      }),
      mergeMap((selectIds) => {
        if (clusterParentUpdate) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(layer)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
      mergeMap((selectIds) => {
        if (clusterParentUpdate) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
    );
  }

  static removeImage(store: Store,
                     id: string,
                     clusterParentUpdate = false) {
    // Get Data
    const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);

    // Get Image
    const image = structuredClone(typedImages[id]);
    
    // Create Image Pipe Object
    const imagePipeModel: FlatModels.PipeEntityModels.ImagePipeObject = {
      data: {},
      image,
    };
    
    // Data
    const removeAnnotations: string[] = [];
    const removePois: string[] = [];

    // Remove Image
    return of(imagePipeModel).pipe(
      mergeMap((result) => {
        return store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveImage(id)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      }),
      mergeMap((result) => {
        // Get Annotations
        const typedAnnotations = store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
        const annotations = image.annotations.reduce((arr: FlatModels.AnnotationEntityModels.AnnotationEntityType[], annotation: string) => {
          const entity = structuredClone(typedAnnotations[annotation]);
          if (entity && entity.images) {
            entity.images.splice(entity.images.findIndex(id => id === image.id), 1);
            if (entity.images.length > 0) {
              arr.push(entity);
            } else {
              removeAnnotations.push(entity.id);
            }
          }
          return arr;
        }, [] as FlatModels.AnnotationEntityModels.AnnotationEntityType[]);
        if (removeAnnotations.length > 0) {
          return forkJoin(removeAnnotations.map(annotation => FlatFacades.AnnotationEntityFacade.removeAnnotation(store, annotation, true))).pipe(
            mergeMap((annotations: (FlatModels.PipeEntityModels.AnnotationPipeObject | undefined)[]) => {
              const filteredAnnotations = annotations.reduce((arr, annotation) => {
                if (annotation !== undefined) {
                  arr.push(annotation);
                }
                return arr;
              }, [] as FlatModels.PipeEntityModels.AnnotationPipeObject[]);
              return of(Object.assign(result, { annotations: filteredAnnotations }))
            }),
          );
        } else if (annotations.length > 0) {
          return forkJoin(annotations.map((annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) => store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation)))).pipe(
            mergeMap((data) => of(Object.assign(result, {data: data})))
          );
        }
        return of(result);
      }),
      mergeMap((result) => {
        // Get Pois
        const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
        const pois = image.pois.reduce((arr: FlatModels.PointOfInterestEntityModels.PointOfInterest[], poi: string) => {
          const entity = structuredClone(typedPois[poi]);
          if (entity && entity.images) {
            entity.images.splice(entity.images.findIndex((id: string) => id === image.id), 1);
            if (entity.images.length > 0) {
              arr.push(entity);
            } else {
              removePois.push(entity.id);
            }
          }
          return arr;
        }, [] as FlatModels.PointOfInterestEntityModels.PointOfInterest[]);
        if (removePois.length > 0) {
          return forkJoin(removePois.map(poi => FlatFacades.PoiEntityFacade.removePoi(store, poi, true))).pipe(
            mergeMap((data: FlatModels.PipeEntityModels.PointOfInterestPipeObject[]) => of(Object.assign(result, {pois: data}))),
            mergeMap((result) => {
              if (pois.length > 0) {
                return forkJoin(pois.map((poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) => store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)))).pipe(
                  mergeMap((data) => of(Object.assign(result, {data: data})))
                );
              }
              return of(result);
            }),
          );
        } else if (pois.length > 0) {
          return forkJoin(pois.map((poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) => {
            poi.annotationToPointOfInterest = poi.annotationToPointOfInterest.filter(atpoi => !removeAnnotations.includes(atpoi.annotation));
            return store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi))
          })).pipe(
            mergeMap((data) => of(Object.assign(result, {data: data})))
          );
        }
        return of(result);
      }),
      mergeMap((result) => {
        // Get Layer
        const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
        const layer = structuredClone(typedLayers[image.layer]);
        // Get Page
        const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
        const page = typedPages[layer.slide] as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
        // Get Pois
        const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
        const pois = page.pois.reduce((arr, poi) => {
          const entity = structuredClone(typedPois[poi]);
          if (entity && entity.annotationToPointOfInterest.find(atpoi => removeAnnotations.includes(atpoi.annotation))) {
            entity.annotationToPointOfInterest = entity.annotationToPointOfInterest.filter(atpoi => !removeAnnotations.includes(atpoi.annotation));
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.PointOfInterestEntityModels.PointOfInterest[]);
        if (pois.length > 0) {
          return forkJoin(pois.map(poi => store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)))).pipe(
            mergeMap((data) => of(Object.assign(result, {data: data})))
          );
        }
        return of(result);
      }),
      mergeMap((result) =>
        store.dispatch(new FlatActions.ImageEntityActions.RemoveEntity(image)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data})))
        )
      ),
      mergeMap((result) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        // Get Layer
        const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
        const layer = structuredClone(typedLayers[image.layer]);
        // Remove Image From Layer
        layer.images.splice(layer.images.findIndex(id => id === image.id), 1);
        return store.dispatch(new FlatActions.ImageEntityActions.RearrangeImages(layer.images)).pipe(
          mergeMap(() => {
            return store.dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(layer));
          }),
          mergeMap((data) => of(Object.assign(result, {data: data})))
        );
      }),
    );
  }

  static removeImages(store: Store,
                      ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }

    // Remove Images
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => ImageEntityFacade.removeImage(store, id, true)))
      ),
      mergeMap((images: FlatModels.PipeEntityModels.ImagePipeObject[]) => {
        const typedImageLayers = images.reduce((arr, imagePipeObject) => {
          const layer = imagePipeObject.image.layer;
          const entity = arr[layer];
          if (entity) {
            arr[layer].images.push(imagePipeObject.image.id);
          } else {
            arr[layer] = {layer, images: [imagePipeObject.image.id]};
          }
          return arr;
        }, {} as {[id: string]: {layer: string, images: string[]}});
        const imageLayers = Object.values(typedImageLayers);
        if (imageLayers.length > 0) {
          return forkJoin(imageLayers.map(imageLayer => FlatFacades.LayerEntityFacade.removeImagesFromLayer(store, imageLayer.images, imageLayer.layer))).pipe(
            mergeMap(() => {
              return of(images)
            }),
          );
        }
        return of(images);
      }),
    );
  }
  
  static removeAnnotationFromImage(store: Store,
                                   id: string,
                                   imageId: string,
                                   clusterParentUpdate = false) {
    const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    const image = structuredClone(typedImages[imageId]);

    if (!image) {
      return of({});
    }
    
    // Check Index
    const index = image.annotations.findIndex((a: string) => a === id);
    if (index < 0) {
      return of({});
    }
    
    // Update Image
    image.annotations.splice(index, 1);
    
    // Remove Annotation From Image
    return of({image}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      }),
    );
  }
  
  static removeAnnotationsFromImage(store: Store,
                                    ids: string[],
                                    imageId: string,
                                    clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of({imageId});
    }
    
    const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    const image = structuredClone(typedImages[imageId]);
    
    for (const id of ids) {
      // Check Index
      const index = image.annotations.findIndex((a: string) => a === id);
      if (index > -1) {
        // Update Image
        image.annotations.splice(index, 1);
      }
    }
    
    // Remove Annotations From Image
    return of({image}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      }),
    );
  }
  
  static removePoiFromImage(store: Store,
                            id: string,
                            imageId: string,
                            clusterParentUpdate = false) {
    const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    const image = structuredClone(typedImages[imageId]);
    
    // Check Index
    const index = image.pois.findIndex((a: string) => a === id);
    if (index < 0) {
      return of({});
    }
    
    // Update Image
    image.pois.splice(index, 1);
    
    // Remove Poi From Image
    return of({image}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      }),
    );
  }
  
  static removePoisFromImage(store: Store,
                             ids: string[],
                             imageId: string,
                             clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of({imageId});
    }
    
    const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    const image = structuredClone(typedImages[imageId]);
    
    for (const id of ids) {
      // Check Index
      const index = image.pois.findIndex((a: string) => a === id);
      if (index > -1) {
        // Update Image
        image.pois.splice(index, 1);
      }
    }
    
    // Remove Pois From Image
    return of({image}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      }),
    );
  }

  //</editor-fold>
}
